<template>
	<div class="imprint">
		<div class="s-html">
			<h1 class="imprint__title">
				{{ $I18n.trans('legal.imprint.title') }}
			</h1>
			<hr aria-hidden="true">
			<article v-if="hasImprintExternalLinkOverride">
				<a :href="imprintExternalLink">
					{{ imprintExternalLink }}
				</a>
			</article>
			<template v-else>
				<!-- eslint-disable vue/no-v-html -->
				<article
					v-for="(section, i) in imprintSections"
					:key="`imprint-section-${i}`"
				>
					<component
						:is="section.headingLevel"
						v-if="section.headingLevel !== 'none' && section.title"
					>
						{{ section.title }}
					</component>
					<div v-html="section.content" />
				</article>
			</template>
		</div>
		<br>
		<hr aria-hidden="true">
	</div>
</template>

<script>
const HEADING_LEVELS = ['none', 'h1', 'h2', 'h3'];

export default {
	name:     'Imprint',
	computed: {
		clientImprintKey() {
			if (window?.isSportPass) {
				return 'sportpass';
			}
			if (window?.isAtv) {
				return 'atv';
			}
			if (window?.isKroneClient || window?.isKrone) {
				return 'krone';
			}
			return 'asport';
		},
		defaultImprintSectionsTranslationKey() {
			return `legal.imprint.${this.clientImprintKey}.sections`;
		},
		hasImprintOverrideEnabled() {
			return !!(
				this.$arenaConfig?.isCustomDomain &&
				this.imprintOverrideMode !== 'none'
			);
		},
		imprintOverrideMode() {
			const mode = (this.$arenaConfig?.imprintOverrideMode || 'none').toString().toLowerCase();
			return ['none', 'sections', 'external_link'].includes(mode) ? mode : 'none';
		},
		imprintExternalLink() {
			return this.resolveLocalizedValue(this.$arenaConfig?.imprintOverrideExternalLinks, true).trim();
		},
		hasImprintExternalLinkOverride() {
			return this.hasImprintOverrideEnabled &&
				this.imprintOverrideMode === 'external_link' &&
				/^https?:\/\//i.test(this.imprintExternalLink);
		},
		hasImprintSectionsOverride() {
			return this.hasImprintOverrideEnabled &&
				this.imprintOverrideMode === 'sections' &&
				Array.isArray(this.$arenaConfig?.imprintOverrideSections) &&
				this.$arenaConfig.imprintOverrideSections.length > 0;
		},
		imprintSections() {
			if (this.hasImprintSectionsOverride) {
				return this.normalizeSections(this.$arenaConfig.imprintOverrideSections, true);
			}

			const defaultSections = this.getTranslatedSections(this.defaultImprintSectionsTranslationKey);
			if (defaultSections) {
				return this.normalizeSections(defaultSections, false);
			}

			// Fallback for new clients
			return this.normalizeSections(this.getTranslatedSections('legal.imprint.asport.sections') || [], false);
		},
	},
	mounted() {
		if (this.hasImprintExternalLinkOverride) {
			if (this.imprintExternalLink !== window.location.href) {
				window.location.replace(this.imprintExternalLink);
			}
		}
	},
	methods: {
		getTranslatedSections(translationKey) {
			const translations = this.$I18n.trans(translationKey);
			return Array.isArray(translations) ? translations : null;
		},
		getHeadingLevel(value, hasTitle) {
			const level = (value || '').toString().toLowerCase();
			if (HEADING_LEVELS.includes(level)) {
				return level;
			}
			return hasTitle ? 'h2' : 'none';
		},
		resolveLocalizedValue(value, isOverride) {
			if (isOverride && value && typeof value === 'object') {
				return this.$Helpers.getTranslatedContent(value);
			}
			return value || '';
		},
		normalizeSections(sections, isOverride) {
			if (!Array.isArray(sections)) {
				return [];
			}

			return sections.map((section) => {
				const title = this.resolveLocalizedValue(section?.title, isOverride);
				const content = this.resolveLocalizedValue(section?.content, isOverride);
				return {
					headingLevel: this.getHeadingLevel(section?.headingLevel || section?.heading_level, !!title),
					title,
					content,
				};
			}).filter(section => section.title || section.content);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.imprint {
	margin-top: $sp7;
	margin-bottom: $sp7;
}

.imprint__title {
	@include font(primary, light, normal, fs-180);
	margin-top: 0;
	margin-bottom: $sp5;
	letter-spacing: initial; // stylelint-disable-line property-disallowed-list

	@include size(lg-up) {
		margin-bottom: $sp6;
	}
}
</style>
